import type { ConsultationJobDetailsViewModel } from '@/features/jobs/details/consultation-type/ConsultationJobDetailsViewModel'
import { useLoaderData, useNavigate } from 'react-router'
import type { JobDetailsViewModel } from '@/features/jobs/details/JobDetailsViewModel'
import { observer } from 'mobx-react-lite'
import { useActivation } from '@/hooks/view-model-hooks'
import { links } from '@/utils/link'
import { useIsSpacious } from '@/hooks/viewport-hooks'
import {
  Wizard,
  WizardBottomBar,
  WizardBottomBarSection,
  WizardBottomBarSpacer,
  WizardContentHeader,
  WizardContentHeaderDescription,
  WizardContentHeaderTitle,
  WizardPageAnimatedContent,
  WizardTopBar,
  WizardTopBarLogo,
  WizardPageSingleColumnLayout,
  WizardPageSingleColumnRow,
  WizardContentFooter,
} from '@/components/Wizard'
import {
  useDisableGlobalNavigationIndicator,
  useHideGlobalNavbar,
} from '@/features/shell/global-navigation/GlobalNavigationContainer'
import { useRootStore } from '@/contexts/root-store-context'
import { Button } from '@/components/Button'
import { IconButton } from '@/components/IconButton'
import { AvailableDaysListContainer } from '@/features/jobs/details/consultation/components/AvailabilitySelector/AvailableDaysListContainer'
import { AvailableTimesListContainer } from '@/features/jobs/details/consultation/components/AvailabilitySelector/AvailableTimesListContainer'
import { useObservable, useObservableValue } from '@/hooks/rx-hooks'
import { ButtonGroup } from '@/components/ButtonGroup'
import { useScreenHeaderTotalOffset } from '@/components/ScreenHeader'
import type ConsultationSchedulerViewModel from '@/features/jobs/details/consultation/ConsultationSchedulerViewModel'
import { MagicBorder } from '@/components/MagicBorder'
import { useElementSize } from '@/hooks/element-hooks'
import { useMotionValue, useScroll, useTransform } from 'motion/react'
import { mediaQueryMatchStream } from '@/utils/browser-util'
import type { ConsultationInfoGatheringViewModel } from '@/features/jobs/details/consultation-type/info-gathering/ConsultationInfoGatheringViewModel'
import { useDocumentTitle } from '@/hooks/document-title'
import {
  InfoGatheringWizardChatIconButtonContainer,
  InfoGatheringWizardConversationDialogContainer,
  InfoGatheringWizardConversationSidePanelContainer,
  useInfoGatheringWizardConversationState,
} from '@/features/jobs/details/tax-type/info-gathering/components/InfoGatheringConversation'
import { DefaultJobConversationContainer } from '@/features/jobs/details/components/DefaultJobConversationContainer'
import { useProvideSupportWidgetHidden } from '@/features/support/components/ChatSupportSpaceManager'
import { useLightDarkFn } from '@/features/shell/theming/color-scheme-hooks'
import { usePageExitMethod } from '@/features/shell/exiting/exit-method-hooks'

export default observer(function ConsultationInfoGathering() {
  const isSpacious = useIsSpacious()
  const navigate = useNavigate()

  const { vm } = useLoaderData<{ vm: JobDetailsViewModel }>()
  const consultationVm = vm.typeVm as ConsultationJobDetailsViewModel
  const infoGatheringVm = consultationVm.infoGatheringVm

  useDocumentTitle(vm.job.title)
  useActivation(vm)
  useActivation(consultationVm.jobConversationVm)

  useHideGlobalNavbar(true)
  useDisableGlobalNavigationIndicator(true)

  useObservable(infoGatheringVm.infoGatheringCompleted$, () => {
    void navigate(links.jobDetails(vm.job.id))
  })

  const isChatAvailable =
    consultationVm.jobConversationVm.isAvailableInInfoGathering
  useProvideSupportWidgetHidden(isChatAvailable)
  const { isChatOpen, setIsChatOpen, openChat, closeChat } =
    useInfoGatheringWizardConversationState({
      isChatAvailable,
      disableOffside: true,
    })

  const testId = 'consultation-info-gathering'

  return (
    <>
      <Wizard testId={testId}>
        <TopBarContainer
          title="Select your availability"
          consultationVm={consultationVm}
          isChatAvailable={isChatAvailable}
          isChatOpen={isChatOpen}
          onOpenChat={openChat}
        />
        <WizardPageAnimatedContent animationKey="default">
          <WizardPageSingleColumnLayout>
            <WizardPageSingleColumnRow>
              <WizardContentHeader>
                <WizardContentHeaderTitle>
                  Select your availability
                </WizardContentHeaderTitle>
                <WizardContentHeaderDescription>
                  Select the days and times that suit you best. A Pro will
                  schedule a {consultationVm.consultation?.requestedLength}
                  -minute consultation call based on your availability.
                </WizardContentHeaderDescription>
              </WizardContentHeader>
            </WizardPageSingleColumnRow>
            <StickyAvailabilityDays
              vm={infoGatheringVm.consultationSchedulerVm}
              testId={testId}
            />
            <WizardPageSingleColumnRow>
              <AvailableTimesListContainer
                spacingX="none"
                vm={infoGatheringVm.consultationSchedulerVm}
                testId={`${testId}-times-list`}
              />

              <WizardContentFooter />
            </WizardPageSingleColumnRow>
          </WizardPageSingleColumnLayout>
          {isSpacious && isChatAvailable && (
            <ConversationSidePanelContainer
              consultationVm={consultationVm}
              isOpen={isChatOpen}
              onOpenChange={setIsChatOpen}
            />
          )}
        </WizardPageAnimatedContent>

        <BottomBarContainer vm={infoGatheringVm} />
      </Wizard>

      <ConversationDialogContainer
        consultationVm={consultationVm}
        isOpen={isChatOpen && !isSpacious}
        onClose={closeChat}
      />
    </>
  )
})

interface TopBarContainerProps {
  title: string
  bordered?: boolean
  consultationVm?: ConsultationJobDetailsViewModel
  isChatAvailable?: boolean
  isChatOpen?: boolean
  onOpenChat?: () => unknown
}

const TopBarContainer = observer(function TopBarContainer({
  title,
  bordered,
  consultationVm,
  isChatAvailable,
  isChatOpen,
  onOpenChat,
}: TopBarContainerProps) {
  const isSpacious = useIsSpacious()
  const {
    shell: { shellInfo },
  } = useRootStore()
  const lightDark = useLightDarkFn()
  const { isExitMethodVisible, exitMethodProps } = usePageExitMethod()

  return (
    <WizardTopBar
      title={title}
      borderVisibility={bordered ? 'auto' : 'none'}
      left={
        isSpacious ? (
          <WizardTopBarLogo
            src={lightDark(shellInfo.iconUrl, shellInfo.invertedIconUrl)}
            alt={shellInfo.name}
            {...(isExitMethodVisible && exitMethodProps)}
          />
        ) : (
          isExitMethodVisible && (
            <IconButton name="back" label="Back" {...exitMethodProps} />
          )
        )
      }
      right={
        isSpacious ? (
          <ButtonGroup spacing="lg">
            {isChatAvailable && consultationVm && onOpenChat && (
              <InfoGatheringWizardChatIconButtonContainer
                isChatAvailable={isChatAvailable}
                isChatOpen={isChatOpen || false}
                conversationVm={consultationVm.jobConversationVm.conversationVm}
                onOpenPress={onOpenChat}
              />
            )}
            {isExitMethodVisible && (
              <Button
                variant="outline"
                label="Exit"
                iconLeft="close"
                {...exitMethodProps}
              />
            )}
          </ButtonGroup>
        ) : (
          isChatAvailable &&
          consultationVm &&
          onOpenChat && (
            <InfoGatheringWizardChatIconButtonContainer
              isChatAvailable={isChatAvailable}
              isChatOpen={isChatOpen || false}
              conversationVm={consultationVm.jobConversationVm.conversationVm}
              onOpenPress={onOpenChat}
            />
          )
        )
      }
    />
  )
})

const sufficientSpaceForPaddedDaySelector$ = mediaQueryMatchStream(
  'screen and (min-width: 600px)',
)

interface StickyAvailabilityDaysProps {
  vm: ConsultationSchedulerViewModel
  testId: string
}

const StickyAvailabilityDays = observer(function StickyAvailabilityDays({
  vm,
  testId,
}: StickyAvailabilityDaysProps) {
  const { connectRef, sizeMotionValues, size } = useElementSize()
  const { scrollY } = useScroll()
  const offset = useScreenHeaderTotalOffset() + size.height - 12
  const progress = useTransform(scrollY, [offset, offset + 100], [0, 1])
  const full = useMotionValue(100)

  const isSufficientSpace = useObservableValue(
    sufficientSpaceForPaddedDaySelector$,
  )

  return (
    <>
      <WizardPageSingleColumnRow
        sticky
        ref={connectRef}
        spacingX={isSufficientSpace ? '3xl' : 'base'}
        border={
          <MagicBorder
            position="static"
            progress={isSufficientSpace ? progress : full}
            contentWidth={sizeMotionValues.width}
            contentHeight={sizeMotionValues.height}
          />
        }
      >
        <AvailableDaysListContainer vm={vm} testId={`${testId}-days-list`} />
      </WizardPageSingleColumnRow>
    </>
  )
})

interface BottomBarContainerProps {
  vm: ConsultationInfoGatheringViewModel
}

const BottomBarContainer = observer(function BottomBarContainer({
  vm,
}: BottomBarContainerProps) {
  const isSpacious = useIsSpacious()
  const { isExitMethodVisible, exitMethodProps } = usePageExitMethod()

  return (
    <WizardBottomBar progress={80}>
      <WizardBottomBarSection>
        {!isSpacious && isExitMethodVisible && (
          <ButtonGroup>
            <Button variant="text" label="Exit" {...exitMethodProps} />
          </ButtonGroup>
        )}
      </WizardBottomBarSection>
      <WizardBottomBarSpacer />
      <WizardBottomBarSection>
        <Button
          label="Connect with a Pro"
          isDisabled={!vm.canComplete}
          isPending={vm.completeInfoGathering.pending}
          onPress={vm.completeInfoGathering}
          testId="consultation-info-gathering-submit-button"
        />
      </WizardBottomBarSection>
    </WizardBottomBar>
  )
})

interface ConversationSidePanelContainerProps {
  consultationVm: ConsultationJobDetailsViewModel
  isOpen: boolean
  onOpenChange: (isOpen: boolean) => unknown
}

/**
 * Conversation Side Panel Container
 */
const ConversationSidePanelContainer = observer(
  function ConversationSidePanelContainer({
    consultationVm,
    isOpen,
    onOpenChange,
  }: ConversationSidePanelContainerProps) {
    return (
      <InfoGatheringWizardConversationSidePanelContainer
        isOpen={isOpen}
        onOpenChange={onOpenChange}
        positioning="floating-tray"
      >
        <DefaultJobConversationContainer
          vm={consultationVm.jobConversationVm}
        />
      </InfoGatheringWizardConversationSidePanelContainer>
    )
  },
)

interface ConversationDialogContainerProps {
  consultationVm: ConsultationJobDetailsViewModel
  isOpen: boolean
  onClose: () => unknown
}

/**
 * Conversation Dialog Container
 */
const ConversationDialogContainer = observer(
  function ConversationDialogContainer({
    consultationVm,
    isOpen,
    onClose,
  }: ConversationDialogContainerProps) {
    return (
      <InfoGatheringWizardConversationDialogContainer
        isOpen={isOpen}
        onClose={onClose}
      >
        <DefaultJobConversationContainer
          vm={consultationVm.jobConversationVm}
        />
      </InfoGatheringWizardConversationDialogContainer>
    )
  },
)
